package songwritersnotebook.app.utils

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.functions.functions
import dev.gitlive.firebase.auth.auth
import songwritersnotebook.app.callIfJs
import songwritersnotebook.app.getPlatform
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.client.call.*
import kotlinx.serialization.Serializable

object CloudFunctionService {
    private val functions = Firebase.functions("us-central1")

    suspend fun grantInitialMonthlyCredits(tier: String) {
        val data = mapOf("tier" to tier)
        try {
            val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
            
            if (isWeb) {
                // Pass directly to global bridge
                val token = dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(false)
                songwritersnotebook.app.callIfJs("grantInitialMonthlyCredits", Any(), data, token)
            } else {
                val callable = functions.httpsCallable("grantInitialMonthlyCredits")
                callable.invoke(data)
            }
            println("CloudFunctionService: Successfully triggered grantInitialMonthlyCredits for tier $tier.")
        } catch (e: Exception) {
            println("CloudFunctionService: Error calling grantInitialMonthlyCredits: $e")
        }
    }

    @Serializable
    data class CloudRhymeResult(val word: String, val score: Int)

    suspend fun getRhymes(word: String): Map<String, Int> {
        return try {
            val httpClient = songwritersnotebook.app.di.PlatformDependencyProvider().getHttpClient()
            val url = "https://getrhymes-7f6uyaw35q-uc.a.run.app"
            
            val response: io.ktor.client.statement.HttpResponse = httpClient.post(url) {
                contentType(ContentType.Application.Json)
                setBody(mapOf("word" to word))
            }
            
            if (response.status.value in 200..299) {
                val list = response.body<List<CloudRhymeResult>>()
                list.associate { it.word to it.score }
            } else {
                println("CloudFunctionService: getRhymes failed with status ${response.status}")
                emptyMap()
            }
        } catch (e: Exception) {
            println("CloudFunctionService: Error calling getRhymes: $e")
            emptyMap()
        }
    }

    suspend fun getPronunciations(words: List<String>): Map<String, List<String>> {
        val startTime = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
        println("TELEMETRY_CLIENT [getPronunciations]: Requesting ${words.size} words.")
        
        return try {
            val httpClient = songwritersnotebook.app.di.PlatformDependencyProvider().getHttpClient()
            val url = "https://getpronunciations-7f6uyaw35q-uc.a.run.app"
            
            val response: io.ktor.client.statement.HttpResponse = httpClient.post(url) {
                contentType(ContentType.Application.Json)
                setBody(mapOf("words" to words))
            }
            
            val duration = kotlinx.datetime.Clock.System.now().toEpochMilliseconds() - startTime
            
            if (response.status.value in 200..299) {
                println("TELEMETRY_CLIENT [getPronunciations]: Success. Duration: ${duration}ms")
                response.body<Map<String, List<String>>>()
            } else {
                println("TELEMETRY_CLIENT [getPronunciations]: Failed with status ${response.status}. Duration: ${duration}ms")
                emptyMap()
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            println("TELEMETRY_CLIENT [getPronunciations]: Cancelled.")
            throw e
        } catch (e: Exception) {
            println("TELEMETRY_CLIENT [getPronunciations]: Error calling getPronunciations: $e")
            emptyMap()
        }
    }

    suspend fun createStripePortalSession(): String? {
        return try {
            val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
            val data = mapOf("returnUrl" to "https://songwritersnotebook.app/subscription")
            
            val result: Any? = if (isWeb) {
                val token = dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(false)
                songwritersnotebook.app.callIfJs("createStripePortalSession", Any(), data, token)
            } else {
                val callable = functions.httpsCallable("createStripePortalSession")
                callable.invoke(data)
            }
            
            if (isWeb) {
                songwritersnotebook.app.extractUrlFromJsResult(result)
            } else {
                val response = (result as dev.gitlive.firebase.functions.HttpsCallableResult).data<Map<String, String>>()
                response["url"]
            }
        } catch (e: Exception) {
            println("CloudFunctionService: Error creating portal session: $e")
            null
        }
    }

    suspend fun requestAccountDeletion() {
        try {
            val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
            if (isWeb) {
                val token = dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(false)
                songwritersnotebook.app.callIfJs("requestAccountDeletion", Any(), emptyMap<String, String>(), token)
            } else {
                val callable = functions.httpsCallable("requestAccountDeletion")
                callable.invoke()
            }
            println("CloudFunctionService: Successfully requested account deletion.")
        } catch (e: Exception) {
            println("CloudFunctionService: Error requesting account deletion: $e")
        }
    }

    suspend fun cancelAccountDeletion() {
        try {
            val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
            if (isWeb) {
                val token = dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(false)
                songwritersnotebook.app.callIfJs("cancelAccountDeletion", Any(), emptyMap<String, String>(), token)
            } else {
                val callable = functions.httpsCallable("cancelAccountDeletion")
                callable.invoke()
            }
            println("CloudFunctionService: Successfully cancelled account deletion.")
        } catch (e: Exception) {
            println("CloudFunctionService: Error cancelling account deletion: $e")
        }
    }

    suspend fun awardAdCredits() {
        try {
            val isWeb = songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true)
            if (isWeb) {
                val token = dev.gitlive.firebase.Firebase.auth.currentUser?.getIdToken(false)
                songwritersnotebook.app.callIfJs("awardAdCredits", Any(), emptyMap<String, String>(), token)
            } else {
                val callable = functions.httpsCallable("awardAdCredits")
                callable.invoke()
            }
            println("CloudFunctionService: Successfully awarded ad credits.")
        } catch (e: Exception) {
            println("CloudFunctionService: Error awarding ad credits: $e")
        }
    }
}
